/*
 * VitaFin — card "Lisää koriin" without leaving the page (colour build 2026-09-27).
 *
 * The homepage grid (vitafin-product-grid) and the site-wide card button (blocks/vf-card-button)
 * post a plain Shopify product form, which used to jump to /cart. Here the submit goes over AJAX
 * instead, and Horizon's own CartLinesUpdateEvent is dispatched so the header bubble, its
 * role="status" count and the cart drawer update exactly as after a product-page add.
 *
 * The button (.vf-atc) turns forest with "Lisätty" + a drawn tick for about 2 s (vf-base.css), and
 * one shared visually hidden role="status" says "<product> lisätty ostoskoriin". No aria-live on the
 * button itself. On any failure the form is submitted the normal way, so Shopify's cart page shows
 * the real error and the add is never silently lost.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const HOLD_MS = 2000;
const FORMS = 'form.vf-card-btn__form, form.vf-pg__form';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** @type {HTMLElement | null} */
let statusEl = null;

function announce(text) {
  if (!statusEl) {
    statusEl = document.createElement('div');
    statusEl.className = 'visually-hidden';
    statusEl.setAttribute('role', 'status');
    document.body.appendChild(statusEl);
  }
  // Clear first so the same message twice in a row is still announced.
  statusEl.textContent = '';
  requestAnimationFrame(() => {
    statusEl.textContent = text;
  });
}

/** Forest "Lisätty" for HOLD_MS, plus the spoken message built in Liquid (data-vf-added). */
function showAdded(button) {
  button.dataset.added = 'true';
  if (button.dataset.vfAdded) announce(button.dataset.vfAdded);
  window.clearTimeout(Number(button.dataset.vfTimer));
  button.dataset.vfTimer = String(window.setTimeout(() => button.removeAttribute('data-added'), HOLD_MS));
}

document.addEventListener('submit', async (event) => {
  const form = /** @type {HTMLFormElement} */ (event.target);
  if (!(form instanceof HTMLFormElement) || !form.matches(FORMS)) return;
  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button.vf-atc'));
  if (!button || typeof fetch !== 'function') return;

  event.preventDefault();
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');

  const formData = new FormData(form);
  const variantId = String(formData.get('id') || '');
  const deferred = CartLinesUpdateEvent.createPromise();

  let inCart = false;
  try {
    const added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    if (!added.ok) throw new Error(`cart/add ${added.status}`);
    inCart = true;
    showAdded(button);

    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'vf-card-add', itemCount: 1 },
    });
  } catch (error) {
    // Added but the cart refresh failed: the product IS in the cart, so never submit again (that would
    // add it twice). The header count catches up on the next page load.
    if (!inCart) {
      console.warn('[vf-card-add] falling back to a normal submit:', error);
      HTMLFormElement.prototype.submit.call(form);
    }
  } finally {
    button.removeAttribute('aria-busy');
  }
});
